import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  ImagePlus,
  ImageUp,
  Italic,
  Link2,
  List,
  ListOrdered,
  RotateCw,
  Underline,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onUploadLocalImage?: (file: File) => Promise<string>;
}

type OverlayRect = { top: number; left: number; width: number; height: number } | null;
type ResizeDirection = 'top' | 'right' | 'bottom' | 'left';

export function RichTextEditor({ value, onChange, placeholder, onUploadLocalImage }: RichTextEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastHtmlRef = useRef<string>('');
  const localImageInputRef = useRef<HTMLInputElement | null>(null);
  const selectedImageRef = useRef<HTMLImageElement | null>(null);
  const [imageSelected, setImageSelected] = useState(false);
  const [overlayRect, setOverlayRect] = useState<OverlayRect>(null);
  const [imageWidth, setImageWidth] = useState(60);
  const [imageRotate, setImageRotate] = useState(0);
  const [imageShift, setImageShift] = useState(0);
  const [imageShiftY, setImageShiftY] = useState(0);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const nextValue = value || '';
    const isFocused = document.activeElement === editor;

    // Keep caret stable while typing: only write into DOM when value changed externally.
    if (!isFocused && nextValue !== lastHtmlRef.current) {
      editor.innerHTML = nextValue;
      lastHtmlRef.current = nextValue;
    }
  }, [value]);

  const focusUbahor = () => {
    editorRef.current?.focus();
  };

  const syncUbahorValue = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const html = editor.innerHTML;
    lastHtmlRef.current = html;
    onChange(html);
  };

  const normalizeImages = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.querySelectorAll('img').forEach((img) => {
      if (!img.style.maxWidth) img.style.maxWidth = '100%';
      if (!img.style.height) img.style.height = 'auto';
      if (!img.style.width) img.style.width = '60%';
      if (!img.style.display) img.style.display = 'inline-block';
      if (!img.style.objectFit) img.style.objectFit = 'cover';
      if (!img.style.cursor) img.style.cursor = 'move';
      img.draggable = true;
    });
  };

  const buildTransform = (shiftX: number, shiftY: number, rotateDeg: number) => {
    const parts: string[] = [];
    if (shiftX !== 0 || shiftY !== 0) {
      parts.push(`translate(${shiftX}px, ${shiftY}px)`);
    }
    if (rotateDeg !== 0) {
      parts.push(`rotate(${rotateDeg}deg)`);
    }
    return parts.join(' ');
  };

  const parseTransform = (value: string) => {
    const shiftMatch = value.match(/translate\((-?\d+(?:\.\d+)?)px,\s*(-?\d+(?:\.\d+)?)px\)/i);
    const shiftXMatch = value.match(/translateX\((-?\d+(?:\.\d+)?)px\)/i);
    const rotateMatch = value.match(/rotate\((-?\d+(?:\.\d+)?)deg\)/i);

    return {
      shift: shiftMatch ? Math.round(Number(shiftMatch[1])) : (shiftXMatch ? Math.round(Number(shiftXMatch[1])) : 0),
      shiftY: shiftMatch ? Math.round(Number(shiftMatch[2])) : 0,
      rotate: rotateMatch ? Math.round(Number(rotateMatch[1])) : 0,
    };
  };

  const clearSelectedImage = () => {
    const selected = selectedImageRef.current;
    if (selected) {
      selected.style.outline = '';
      selected.style.outlineOffset = '';
    }
    selectedImageRef.current = null;
    setImageSelected(false);
    setOverlayRect(null);
  };

  const refreshImageOverlay = () => {
    const image = selectedImageRef.current;
    const editor = editorRef.current;
    if (!image || !editor) {
      setOverlayRect(null);
      return;
    }

    const editorRect = editor.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    setOverlayRect({
      top: imageRect.top - editorRect.top,
      left: imageRect.left - editorRect.left,
      width: imageRect.width,
      height: imageRect.height,
    });
  };

  useEffect(() => {
    const handleLihatportChange = () => refreshImageOverlay();
    window.addEventListener('resize', handleLihatportChange);
    window.addEventListener('scroll', handleLihatportChange, true);
    return () => {
      window.removeEventListener('resize', handleLihatportChange);
      window.removeEventListener('scroll', handleLihatportChange, true);
    };
  }, []);

  const selectImage = (img: HTMLImageElement) => {
    clearSelectedImage();
    selectedImageRef.current = img;
    img.style.outline = '2px solid #16a34a';
    img.style.outlineOffset = '2px';

    const editor = editorRef.current;
    const widthPercent = editor && img.clientWidth
      ? (img.clientWidth / Math.max(editor.clientWidth, 1)) * 100
      : (img.style.width.endsWith('%') ? Number(img.style.width.replace('%', '')) : 60);
    const transform = parseTransform(img.style.transform || '');
    setImageWidth(Number.isFinite(widthPercent) ? Math.round(widthPercent) : 60);
    setImageShift(transform.shift);
    setImageShiftY(transform.shiftY);
    setImageRotate(transform.rotate);
    setImageSelected(true);
    refreshImageOverlay();
  };

  const run = (command: string, commandValue?: string) => {
    focusUbahor();
    document.execCommand(command, false, commandValue);
    normalizeImages();
    syncUbahorValue();
  };

  const insertLink = () => {
    const url = window.prompt('Masukkan URL link (https://...)');
    if (!url) return;
    run('createLink', url);
  };

  const insertImage = () => {
    const url = window.prompt('Masukkan URL gambar (https://...)');
    if (!url) return;
    run('insertImage', url);
  };

  const triggerLocalImageSelect = () => {
    localImageInputRef.current?.click();
  };

  const insertLocalImage = async (file: File | null) => {
    if (!file) return;

    if (onUploadLocalImage) {
      try {
        const uploadedUrl = await onUploadLocalImage(file);
        if (uploadedUrl) {
          run('insertImage', uploadedUrl);
        }
        return;
      } catch (error) {
        console.error('Failed to upload local image:', error);
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      if (!src) return;
      run('insertImage', src);
    };
    reader.readAsDataURL(file);
  };

  const applyImageWidth = (next: number) => {
    const image = selectedImageRef.current;
    const editor = editorRef.current;
    if (!image) return;
    if (editor) {
      const px = (next / 100) * Math.max(editor.clientWidth, 1);
      image.style.width = `${Math.round(px)}px`;
      image.style.maxWidth = '100%';
    } else {
      image.style.width = `${next}%`;
      image.style.maxWidth = '100%';
    }
    image.style.height = 'auto';
    setImageWidth(next);
    refreshImageOverlay();
    syncUbahorValue();
  };

  const applyImageRotate = (next: number) => {
    const image = selectedImageRef.current;
    if (!image) return;
    image.style.transform = buildTransform(imageShift, imageShiftY, next);
    setImageRotate(next);
    refreshImageOverlay();
    syncUbahorValue();
  };

  const moveImage = (delta: number) => {
    const image = selectedImageRef.current;
    const editor = editorRef.current;
    if (!image || !editor) return;
    const next = imageShift + delta;
    image.style.transform = buildTransform(next, imageShiftY, imageRotate);
    const imageRect = image.getBoundingClientRect();
    const editorRect = editor.getBoundingClientRect();
    let adjustX = next;
    if (imageRect.left < editorRect.left) {
      adjustX += editorRect.left - imageRect.left;
    }
    if (imageRect.right > editorRect.right) {
      adjustX -= imageRect.right - editorRect.right;
    }
    if (adjustX !== next) {
      image.style.transform = buildTransform(adjustX, imageShiftY, imageRotate);
    }
    setImageShift(adjustX);
    refreshImageOverlay();
    syncUbahorValue();
  };

  const getLineHeightPx = () => {
    const editor = editorRef.current;
    if (!editor) return 24;
    const lineHeight = window.getComputedStyle(editor).lineHeight;
    const parsed = Number.parseFloat(lineHeight);
    return Number.isFinite(parsed) ? parsed : 24;
  };

  const snapToLine = (value: number) => {
    const line = Math.max(1, getLineHeightPx());
    return Math.round(value / line) * line;
  };

  const startDragImage = (event: ReactMouseEvent) => {
    const image = selectedImageRef.current;
    const editor = editorRef.current;
    if (!image || !editor) return;
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const baseX = imageShift;
    const baseY = imageShiftY;

    const onMove = (e: MouseEvent) => {
      const nextX = baseX + (e.clientX - startX);
      const nextY = snapToLine(baseY + (e.clientY - startY));
      image.style.transform = buildTransform(nextX, nextY, imageRotate);
      const imageRect = image.getBoundingClientRect();
      const editorRect = editor.getBoundingClientRect();
      let adjustX = nextX;
      let adjustY = nextY;

      if (imageRect.left < editorRect.left) {
        adjustX += editorRect.left - imageRect.left;
      }
      if (imageRect.right > editorRect.right) {
        adjustX -= imageRect.right - editorRect.right;
      }
      if (imageRect.top < editorRect.top) {
        adjustY += editorRect.top - imageRect.top;
      }
      if (imageRect.bottom > editorRect.bottom) {
        adjustY -= imageRect.bottom - editorRect.bottom;
      }

      if (adjustX !== nextX || adjustY !== nextY) {
        image.style.transform = buildTransform(adjustX, adjustY, imageRotate);
      }

      setImageShift(Math.round(adjustX));
      setImageShiftY(Math.round(adjustY));
      refreshImageOverlay();
      syncUbahorValue();
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  };

  const startResize = (direction: ResizeDirection, event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const image = selectedImageRef.current;
    const editor = editorRef.current;
    if (!image || !editor) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = image.getBoundingClientRect().width;
    const startHeight = image.getBoundingClientRect().height || 1;
    const ratio = startWidth / startHeight;

    const onMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      let nextWidth = startWidth;

      if (direction === 'right') nextWidth = startWidth + deltaX;
      if (direction === 'left') nextWidth = startWidth - deltaX;
      if (direction === 'bottom') nextWidth = startWidth + deltaY * ratio;
      if (direction === 'top') nextWidth = startWidth - deltaY * ratio;

      const minWidth = 80;
      const maxWidth = Math.max(editor.clientWidth, 120);
      nextWidth = Math.min(Math.max(nextWidth, minWidth), maxWidth);

      image.style.width = `${Math.round(nextWidth)}px`;
      image.style.height = 'auto';
      image.style.maxWidth = '100%';
      const widthPct = Math.round((nextWidth / Math.max(editor.clientWidth, 1)) * 100);
      setImageWidth(Math.max(20, Math.min(widthPct, 100)));
      refreshImageOverlay();
      syncUbahorValue();
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = direction === 'left' || direction === 'right' ? 'ew-resize' : 'ns-resize';
  };

  return (
    <div className="overflow-hidden rounded-md border">
      <div className="relative z-30 flex flex-wrap items-center gap-1 border-b bg-muted/40 p-2">
        <Select onValueChange={(v) => run('fontName', v)} defaultValue="Arial">
          <SelectTrigger className="h-8 w-[130px]">
            <SelectValue placeholder="Font" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Arial">Arial</SelectItem>
            <SelectItem value="Georgia">Georgia</SelectItem>
            <SelectItem value="Times New Roman">Times New Roman</SelectItem>
            <SelectItem value="Verdana">Verdana</SelectItem>
            <SelectItem value="Courier New">Courier New</SelectItem>
          </SelectContent>
        </Select>
        <Select onValueChange={(v) => run('fontUkuran', v)} defaultValue="3">
          <SelectTrigger className="h-8 w-[90px]">
            <SelectValue placeholder="Ukuran" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">10px</SelectItem>
            <SelectItem value="2">13px</SelectItem>
            <SelectItem value="3">16px</SelectItem>
            <SelectItem value="4">18px</SelectItem>
            <SelectItem value="5">24px</SelectItem>
            <SelectItem value="6">32px</SelectItem>
            <SelectItem value="7">48px</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('bold')}>
          <Bold className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('italic')}>
          <Italic className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('underline')}>
          <Underline className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('insertUnorderedList')}>
          <List className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('insertOrderedList')}>
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('justifyLeft')}>
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('justifyCenter')}>
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('justifyRight')}>
          <AlignRight className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => run('justifyFull')}>
          <AlignJustify className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={insertLink}>
          <Link2 className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={insertImage}>
          <ImagePlus className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={triggerLocalImageSelect}>
          <ImageUp className="h-4 w-4" />
        </Button>
        <input
          ref={localImageInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={async (e) => {
            const input = e.currentTarget;
            const file = input.files?.[0] || null;
            try {
              await insertLocalImage(file);
            } finally {
              input.value = '';
            }
          }}
        />
      </div>
      <div ref={containerRef} className="relative z-0">
        <div
          ref={editorRef}
          className="relative z-0 min-h-[280px] overflow-hidden p-4 focus:outline-none"
          contentEditable
          suppressContentEditableWarning
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target instanceof HTMLImageElement) {
              selectImage(target);
              return;
            }
            clearSelectedImage();
          }}
          onInput={(e) => {
            normalizeImages();
            const html = (e.target as HTMLDivElement).innerHTML;
            lastHtmlRef.current = html;
            onChange(html);
            refreshImageOverlay();
          }}
          data-placeholder={placeholder || 'Tulis konten Anda...'}
        />
        {imageSelected && overlayRect && (
          <div
            className="pointer-events-none absolute border-2 border-green-600"
            style={{
              top: overlayRect.top,
              left: overlayRect.left,
              width: overlayRect.width,
              height: overlayRect.height,
            }}
          >
            <button
              type="button"
              className="pointer-events-auto absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border border-green-700 bg-white cursor-grab text-[10px] font-bold text-green-700"
              onMouseDown={startDragImage}
              title="Geser gambar"
            >
              GESER
            </button>
            <button
              type="button"
              className="pointer-events-auto absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-green-700 bg-white cursor-ns-resize"
              onMouseDown={(e) => startResize('top', e)}
            />
            <button
              type="button"
              className="pointer-events-auto absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 translate-x-1/2 rounded-full border border-green-700 bg-white cursor-ew-resize"
              onMouseDown={(e) => startResize('right', e)}
            />
            <button
              type="button"
              className="pointer-events-auto absolute bottom-0 left-1/2 h-3 w-3 -translate-x-1/2 translate-y-1/2 rounded-full border border-green-700 bg-white cursor-ns-resize"
              onMouseDown={(e) => startResize('bottom', e)}
            />
            <button
              type="button"
              className="pointer-events-auto absolute left-0 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-green-700 bg-white cursor-ew-resize"
              onMouseDown={(e) => startResize('left', e)}
            />
          </div>
        )}
        {imageSelected && overlayRect && (
          <div
            className="absolute z-40 rounded-xl border bg-white/95 px-3 py-2 shadow-md backdrop-blur"
            style={{
              left: overlayRect.left + overlayRect.width / 2,
              top: Math.max(8, overlayRect.top - 78),
              transform: 'translateX(-50%)',
            }}
          >
            <div className="mb-1 flex items-center gap-2 text-[11px]">
              <span className="font-medium text-muted-foreground">Gambar dipilih</span>
              <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[10px]" onClick={() => moveImage(-10)}>
                Kiri
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[10px]" onClick={() => moveImage(10)}>
                Kanan
              </Button>
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={clearSelectedImage}>
                Selesai
              </Button>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span>Ukuran</span>
                <input
                  type="range"
                  min={20}
                  max={100}
                  step={1}
                  value={imageWidth}
                  onChange={(e) => applyImageWidth(Number(e.target.value))}
                />
              <span>{imageWidth}%</span>
              <RotateCw className="ml-1 h-3.5 w-3.5" />
              <input
                type="range"
                min={-180}
                max={180}
                step={1}
                value={imageRotate}
                onChange={(e) => applyImageRotate(Number(e.target.value))}
              />
              <span>{imageRotate}deg</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
